import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, first, map, switchMap } from 'rxjs/operators';
import { environment } from '@app/../environments/environment';

export interface LabelLogo {
  /** Printer ready bitmap, embedded into the label template at print time */
  dataUrl: string;
  width: number;
  height: number;
  sourceWidth: number;
  sourceHeight: number;
}

export interface LabelLogoFit {
  widthMm: number;
  heightMm: number;
  warnings: string[];
}

const STORAGE_KEY = 'locLogoBase64';
const TWIPS_PER_INCH = 1440;
const MM_PER_INCH = 25.4;
const PRINTER_DPI = 300;
/** Anything lighter than this prints as blank on a single colour printer */
const INK_THRESHOLD = 220;

@Injectable({
  providedIn: 'root'
})
export class LabelLogoService {
  private endpoint: string = environment.apiUrl + '/image-upload';

  constructor(private http: HttpClient) { }

  public getLabelLogo(): Observable<string> {
    return this.http.get<any>(`${this.endpoint}/label`).pipe(
      first(),
      map(res => res || ''),
      catchError(() => of(''))
    );
  }

  public storeLabelLogo(image: string): Observable<any> {
    return this.http.post<any>(`${this.endpoint}/label`, { image: image }).pipe(first());
  }

  public resetLabelLogo(): Observable<any> {
    return this.http.get<any>(`${this.endpoint}/reset-label`).pipe(first());
  }

  /**
   * The label logo falls back to the site logo so locations that never uploaded
   * one still get branded labels.
   */
  public resolveLogoSource(): Observable<string> {
    return this.getLabelLogo().pipe(
      switchMap(labelLogo => labelLogo
        ? of(labelLogo)
        : this.http.get<any>(`${this.endpoint}`).pipe(first(), map(res => res || ''), catchError(() => of(''))))
    );
  }

  /**
   * Refreshes the cached bitmap the DYMO templates read at print time.
   */
  public cacheForPrinting(): void {
    this.resolveLogoSource().subscribe({
      next: (source) => this.prepare(source).then(logo => this.cache(logo)),
      error: () => this.cache(null)
    });
  }

  public cache(logo: LabelLogo | null): void {
    if(!logo){
      localStorage.removeItem(STORAGE_KEY);
      return;
    }

    try {
      localStorage.setItem(STORAGE_KEY, logo.dataUrl);
    } catch (e) {
      // Logo too large to cache - labels simply print without it
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  /**
   * Turns an uploaded logo into what the printer actually receives: flattened
   * onto white, reduced to pure black and white, trimmed of blank margins and
   * encoded as a BMP, the only image format DYMO renders reliably.
   */
  public prepare(dataUrl: string): Promise<LabelLogo | null> {
    // A file path fallback such as the default logo cannot be embedded in a
    // label, only image data can
    if(!dataUrl || dataUrl.indexOf('data:image/') !== 0){
      return Promise.resolve(null);
    }

    return new Promise(resolve => {
      const image = new Image();
      image.onload = () => resolve(this.build(image));
      image.onerror = () => resolve(null);
      image.src = dataUrl;
    });
  }

  /**
   * How large the logo lands on paper, plus anything that would make it print
   * badly. Bounds are the label template values, in twips.
   */
  public describeFit(logo: LabelLogo | null, boundsWidth: number, boundsHeight: number): LabelLogoFit {
    if(!logo){
      return { widthMm: 0, heightMm: 0, warnings: ['labelLogo.warningBlank'] };
    }

    const scale = Math.min(boundsWidth / logo.width, boundsHeight / logo.height);
    const widthMm = (logo.width * scale) / TWIPS_PER_INCH * MM_PER_INCH;
    const heightMm = (logo.height * scale) / TWIPS_PER_INCH * MM_PER_INCH;

    const warnings: string[] = [];
    if(heightMm < 3){
      warnings.push('labelLogo.warningSmall');
    }
    if(logo.width / logo.height < 1.5){
      warnings.push('labelLogo.warningShape');
    }
    if(logo.width < (boundsWidth / TWIPS_PER_INCH) * PRINTER_DPI * 0.5){
      warnings.push('labelLogo.warningResolution');
    }

    return {
      widthMm: Math.round(widthMm * 10) / 10,
      heightMm: Math.round(heightMm * 10) / 10,
      warnings: warnings
    };
  }

  private build(image: HTMLImageElement): LabelLogo | null {
    const canvas = document.createElement('canvas');
    // Vector logos usually carry no intrinsic size, so rasterise them at a
    // resolution well above what the label needs
    canvas.width = image.naturalWidth || 600;
    canvas.height = image.naturalHeight || 600;

    const context = canvas.getContext('2d');
    if(!context || !canvas.width || !canvas.height){
      return null;
    }

    // Flatten onto white, printers render transparency unpredictably
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    let pixels: Uint8ClampedArray;
    try {
      pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
    } catch (e) {
      return null;
    }

    const ink: boolean[] = new Array(canvas.width * canvas.height);
    let minX = canvas.width, minY = canvas.height, maxX = -1, maxY = -1;

    for(let y = 0; y < canvas.height; y++){
      for(let x = 0; x < canvas.width; x++){
        const index = y * canvas.width + x;
        const i = index * 4;
        // Thermal printers are single colour, so decide per pixel here instead
        // of letting the printer dither a light logo away to nothing
        const luminance = 0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2];
        const isInk = luminance < INK_THRESHOLD;
        ink[index] = isInk;

        if(isInk){
          if(x < minX) minX = x;
          if(x > maxX) maxX = x;
          if(y < minY) minY = y;
          if(y > maxY) maxY = y;
        }
      }
    }

    if(maxX < minX || maxY < minY){
      return null;
    }

    return {
      dataUrl: this.encodeBmp(ink, canvas.width, minX, minY, maxX, maxY),
      width: maxX - minX + 1,
      height: maxY - minY + 1,
      sourceWidth: canvas.width,
      sourceHeight: canvas.height
    };
  }

  /**
   * Writes a 24 bit BMP, the format DYMO stores label images in. Rows are
   * bottom up and padded to a multiple of four bytes.
   */
  private encodeBmp(ink: boolean[], sourceWidth: number, minX: number, minY: number, maxX: number, maxY: number): string {
    const width = maxX - minX + 1;
    const height = maxY - minY + 1;
    const rowSize = (width * 3 + 3) & ~3;
    const pixelArraySize = rowSize * height;
    const fileSize = 54 + pixelArraySize;

    const bytes = new Uint8Array(fileSize);
    const view = new DataView(bytes.buffer);

    bytes[0] = 0x42; bytes[1] = 0x4d;
    view.setUint32(2, fileSize, true);
    view.setUint32(10, 54, true);
    view.setUint32(14, 40, true);
    view.setInt32(18, width, true);
    view.setInt32(22, height, true);
    view.setUint16(26, 1, true);
    view.setUint16(28, 24, true);
    view.setUint32(34, pixelArraySize, true);
    view.setInt32(38, 2835, true);
    view.setInt32(42, 2835, true);

    for(let y = 0; y < height; y++){
      const rowStart = 54 + (height - 1 - y) * rowSize;
      for(let x = 0; x < width; x++){
        const value = ink[(minY + y) * sourceWidth + (minX + x)] ? 0 : 255;
        const offset = rowStart + x * 3;
        bytes[offset] = value;
        bytes[offset + 1] = value;
        bytes[offset + 2] = value;
      }
      for(let pad = width * 3; pad < rowSize; pad++){
        bytes[rowStart + pad] = 255;
      }
    }

    let binary = '';
    for(let i = 0; i < bytes.length; i += 8192){
      binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 8192)));
    }

    return 'data:image/bmp;base64,' + btoa(binary);
  }
}
